import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { SelectionModel } from '@angular/cdk/collections';
import { FormControl } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Contact, ContactsApi, GroupsApi, PagedContactResponse } from 'src/app/hickory-api';
import { Component, Inject, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { PageEvent, MatPaginator } from '@angular/material/paginator';
import { tap, debounceTime, finalize, Subject, takeUntil, of, Observable, catchError } from 'rxjs';

type UserType = 'All' | 'Internal' | 'Public';
export interface SelectContactsDialogData {
  userType?: UserType;
  showUserTypeSelection?: boolean;
  groupId?: number;
  selected?: number[];
}

@Component({
  selector: 'app-contacts-select-dialog',
  templateUrl: './contacts-select-dialog.component.html',
  styleUrls: ['./contacts-select-dialog.component.scss'],
  standalone: false
})
export class ContactsSelectDialogComponent implements OnInit, OnDestroy {
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  availableContacts: Contact[] = [];
  totalContactCount: number = 0;
  defaultPageSize: number = 100;

  isLoading: boolean = false;

  selected: SelectionModel<number> = new SelectionModel<number>(true, []);
  idToContact: Map<number, Contact> = new Map<number, Contact>();

  selectedUserType: UserType = 'All';
  showUserTypeSelection = true;

  searchControl = new FormControl();
  displayedColumns: string[] = ['name', 'email'];

  destroy$: Subject<void> = new Subject<void>();

  get selectedContactIds(): number[] { return this.selected.selected; }
  get selectedContacts(): Contact[] { return this.selected.selected.map((c) => this.idToContact.get(c)); }
  get areSomeSelected(): boolean { return !!this.selected.selected.length; }
  get areAllSelected(): boolean { return !!this.availableContacts?.length && this.availableContacts?.every((c) => this.isSelected(c)); }
  get addContactsText(): string {
    if (!this.selected.selected.length) {
      return this.translation.translate('Groups.Management.Buttons.AddContacts', null, this.locale.language);
    }

    return (
      this.translation.translate('Groups.Management.Buttons.AddContacts', null, this.locale.language) +
      ` (${this.selected.selected.length})`
    );
  }

  get noContactsText(): string {
    if (!this.selectedUserType || this.selectedUserType === 'All') {
      return this.translation.translate('Groups.Management.Contacts.NoContacts', null, this.locale.language);
    }

    return this.translation.translate(
      'Groups.Management.Contacts.NoContactsUserType',
      { userType: this.selectedUserType },
      this.locale.language,
    );
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: SelectContactsDialogData,
    @Inject(L10N_LOCALE) public locale: L10nLocale,
    private readonly translation: L10nTranslationService,
    private readonly groupsApi: GroupsApi,
    private readonly contactsApi: ContactsApi,
  ) {}

  ngOnInit(): void {
    this.selectedUserType = this.data.userType;
    this.showUserTypeSelection = this.data.showUserTypeSelection;
    this.getPreselectedContacts(this.data.selected);
    this.getContacts(1, this.defaultPageSize, '', this.selectedUserType);

    this.searchControl.valueChanges
      .pipe(
        debounceTime(500),
        tap((val: string) => {
          this.getContacts(1, this.paginator?.pageSize, val, this.selectedUserType);
        }),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  isSelected(contact: Contact): boolean { return this.selected.isSelected(contact.id); }
  onSelectionChange(contact: Contact): void { this.selected.toggle(contact.id); }

  getContacts(
    page: number = 1,
    pageSize: number = this.defaultPageSize,
    search: string = this.searchControl.value,
    userType?: UserType,
  ): void {
    this.isLoading = true;

    if (userType === 'All') {
      userType = undefined;
    }

    const groupId = this.data.groupId;

    let request$: Observable<PagedContactResponse>;

    if (groupId) {
      request$ = this.groupsApi.getAvailableContacts(
        groupId,
        userType as undefined | 'Internal' | 'Public',
        search,
        page,
        pageSize,
      );
    } else {
      request$ = this.contactsApi.findContacts({
        contactIds: [],
        includeAsset: false,
        search,
        page,
        pageSize,
        userType: userType as 'Internal' | 'Public' | undefined,
      });
    }

    request$
      .pipe(
        catchError((error) => {
          this.isLoading = false;
          return of(error);
        }),
        tap((contacts: PagedContactResponse) => {
          contacts.items?.forEach((contact) => this.idToContact.set(contact.id, contact));
          this.availableContacts = contacts.items ?? [];
          this.totalContactCount = contacts.totalItemCount;
          this.paginator.pageIndex = contacts.page - 1;
        }),
        takeUntil(this.destroy$),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe();
  }

  getPreselectedContacts(contactIds: number[]) {
    if (!contactIds?.length) {
      return;
    }

    this.contactsApi
      .findContacts({ contactIds, includeAsset: false })
      .pipe(
        takeUntil(this.destroy$),
        tap((contacts) => {
          contacts.items?.forEach((contact) => {
            this.idToContact.set(contact.id, contact);
            this.selected.select(contact.id);
          });
        }),
      )
      .subscribe();
  }

  onToggleAll(): void {
    if (!this.areAllSelected) {
      this.availableContacts.forEach((contact) => this.selected.select(contact.id));
      return;
    }

    this.selected.clear();
  }

  onUserTypeChange(userType?: 'Internal' | 'Public'): void {
    this.selectedUserType = userType;
    this.getContacts(1, this.paginator?.pageSize, this.searchControl.value, userType);
  }

  getFirstEmail(contact: Contact): string {
    if (!contact?.emails?.length) {
      return '';
    }

    return contact?.emails?.[0]?.address;
  }

  onPageChange(event: PageEvent): void {
    this.getContacts(event.pageIndex + 1, event.pageSize, this.searchControl.value, this.selectedUserType);
  }
}
